import { queryOpenRouter } from '@/lib/openrouter';
import { parseJsonResponse } from './ai/ai-utils';

export interface ContentArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DetectContentAreaResult {
  success: boolean;
  pageWidth: number;
  pageHeight: number;
  contentArea: ContentArea;
}

/**
 * Detect letterhead content area using OpenRouter vision AI (GPT-4o).
 * Replaces old Next.js `/api/letterhead/detect-content-area` route.
 */
export async function detectContentArea(
  imageDataUrl: string,
  imageWidth: number,
  imageHeight: number
): Promise<DetectContentAreaResult> {
  // Fixed horizontal margins (12% of page width)
  const fixedX = Math.round(imageWidth * 0.12);
  const fixedWidth = Math.round(imageWidth * 0.76);

  const systemPrompt = `You are an expert at analyzing letterhead designs and document layouts. Your task is to identify the VERTICAL content area (top and bottom boundaries only) where text should be placed, avoiding header graphics, logos, and footer elements.

Analyze the provided letterhead image and return a JSON object with:
1. The TOP boundary (y coordinate in pixels) - where content should START (below any header/logo)
2. The BOTTOM boundary (contentBottom in pixels) - where content should END (above any footer graphics)

Focus ONLY on detecting:
- Where the header/logo graphics END (this determines the top of content area)
- Where the footer graphics BEGIN (this determines the bottom of content area)

The horizontal margins are FIXED at 12% of the page width on each side - you do not need to detect these.

Return ONLY valid JSON in this exact format:
{
  "contentTop": 450,
  "contentBottom": 2850
}

Where:
- contentTop: Y coordinate in pixels where text content should start (below header)
- contentBottom: Y coordinate in pixels where text content should end (above footer)

All coordinates are in pixels relative to the image dimensions (${imageWidth}x${imageHeight}).`;

  const response = await queryOpenRouter({
    messages: [
      { role: 'system', content: systemPrompt },
      {
        role: 'user',
        content: `Analyze this letterhead image (${imageWidth}x${imageHeight} pixels) and detect the vertical content boundaries. Return contentTop (Y where content starts, below header) and contentBottom (Y where content ends, above footer).`,
      },
    ],
    model: 'openai/gpt-4o',
    responseFormat: 'json',
    temperature: 0.3,
  });

  const result = parseJsonResponse(response.message);

  if (result.contentTop === undefined || result.contentBottom === undefined) {
    throw new Error('Invalid AI response structure - expected contentTop and contentBottom');
  }

  const contentTop = Math.max(0, Math.min(result.contentTop as number, imageHeight));
  const contentBottom = Math.max(
    contentTop + 50,
    Math.min(result.contentBottom as number, imageHeight)
  );

  return {
    success: true,
    pageWidth: imageWidth,
    pageHeight: imageHeight,
    contentArea: {
      x: fixedX,
      y: contentTop,
      width: fixedWidth,
      height: contentBottom - contentTop,
    },
  };
}
